import { type AttractionTheme } from '@/config/objects/attraction-themes';
import {
  Button,
  ConfirmDialog,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileText, Globe, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';

interface ThemeResponse {
  success: boolean;
  data: AttractionTheme;
}

const themeTabs = [
  { label: 'Overview', path: 'overview', icon: FileText },
  { label: 'Translations', path: 'translations', icon: Globe },
] as const;

/**
 * Layout for the inline theme detail. Renders Back + Edit/Delete on top,
 * a small NavLink tab strip (Overview / Translations) underneath, and an
 * `<Outlet />` for the active tab's content.
 *
 * Mounted at `/attractions/:id/themes/:themeId` as a child of the parent
 * attraction's `EntityDetailShell`, with this layout's own children
 * mounted at `/overview` and `/translations`.
 */
export function AttractionThemeDetailPage() {
  const { id: attractionId, themeId } = useParams<{
    id: string;
    themeId: string;
  }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Same query key as `<AttractionThemeOverviewTab>` so the two share a
  // single fetch via TanStack Query's request dedup.
  const { data } = useQuery<ThemeResponse>({
    queryKey: ['AttractionTheme', attractionId, themeId],
    queryFn: () =>
      apiClient.get<ThemeResponse>(
        `/attractions/${attractionId}/themes/${themeId}`,
      ),
    enabled: Boolean(attractionId && themeId),
  });
  const theme = data?.data;

  const deleteMutation = useMutation({
    mutationFn: async (
      id: string,
    ): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/attractions/${attractionId}/themes/${id}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['AttractionTheme'] });
        navigate(`/attractions/${attractionId}/themes`);
      } else {
        throw new Error(response.error || 'Failed to delete theme');
      }
    },
  });

  if (!attractionId || !themeId) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold mb-2">Theme Not Found</h2>
        <p className="text-muted-foreground">
          The requested theme could not be found.
        </p>
      </div>
    );
  }

  const themeName = theme?.name || theme?.themeType?.name || 'this theme';

  return (
    // -mt offsets the parent content container's top padding so this
    // tab strip sits flush against the parent attraction's tab bar.
    // The inner nav drops its top border (the parent's bottom border
    // already separates the two strips) and sticks at top: 49px — the
    // height of the parent attraction's tab bar (matches its skeleton's
    // `h-[49px]`) — so both bars stay visible while scrolling content.
    <div className="space-y-4 -mt-4 sm:-mt-6">
      <nav
        className="sticky top-[49px] z-[9] flex items-center justify-between gap-4 border-b -mx-4 sm:-mx-6 px-4 sm:px-6 bg-background"
        aria-label="Theme tabs"
      >
        <div className="flex items-center gap-6 sm:gap-8 min-w-0 overflow-x-auto">
          {/* "All Themes" looks like a tab but navigates out of this
              layout — it's never the active tab here, so the styling
              just matches the inactive look. */}
          <NavLink
            to={`/attractions/${attractionId}/themes`}
            className="flex items-center gap-1.5 py-3 text-sm font-medium -mb-px border-b-2 border-transparent text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
          >
            <ArrowLeft className="h-4 w-4 shrink-0" />
            All Themes
          </NavLink>
          {themeTabs.map((tab) => {
            const TabIcon = tab.icon;
            return (
              <NavLink
                key={tab.path}
                to={`/attractions/${attractionId}/themes/${themeId}/${tab.path}`}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 py-3 text-sm font-medium -mb-px border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'text-primary border-primary'
                      : 'text-muted-foreground hover:text-foreground border-transparent'
                  }`
                }
              >
                <TabIcon className="h-4 w-4 shrink-0" />
                {tab.label}
              </NavLink>
            );
          })}
        </div>
        <div className="flex shrink-0 items-center gap-2 py-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              navigate(`/attractions/${attractionId}/themes/${themeId}/edit`)
            }
          >
            <Pencil className="h-4 w-4" />
            Edit
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        </div>
      </nav>

      <Outlet />

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Theme"
        description={`Are you sure you want to delete "${themeName}"? This cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (!themeId) return;
          toast.promise(deleteMutation.mutateAsync(themeId), {
            loading: 'Deleting theme...',
            success: 'Theme deleted successfully',
            error: (err: Error) => err?.message || 'Failed to delete theme',
          });
        }}
      />
    </div>
  );
}
